<template>
  <div>
    <div
      :class="[$style.base, { [$style.expanded]: expanded }]"
      :style="headerStyle"
      @click="$emit('click', $event)"
      v-test="testId"
    >
      <div :class="[$style.section, $style.sectionLeft]">
        <slot name="left" />
      </div>
      <div :class="[$style.section, $style.sectionRight]">
        <slot name="right" />
        <slot name="actions">
          <Flex
            align="center"
            :gap="0.5"
          >
            <div
              v-if="moreOptions"
              :class="$style.more"
              @click="onMoreClick"
            >
              <BaseMore
                :options="moreOptions"
                :color="moreColor"
                background="transparent"
                @select="$emit('moreSelect', $event)"
              />
            </div>
            <ChevronAnimated
              v-if="showChevron"
              :reversed="expanded"
              :color="chevronColor"
              v-test="`${testId}-item-chevron`"
            />
          </Flex>
        </slot>
      </div>
    </div>
    <div v-if="expanded">
      <template v-if="draggable && items.length">
        <Draggable
          :modelValue="items"
          :disabled="dragDisabled"
          :itemKey="itemKey"
          @update:modelValue="$emit('update:items', $event)"
        >
          <template #item="{ element, index }">
            <div
              :class="[$style.item, lastRowClass(index)]"
              :style="rowStyle"
              v-test="rowTestId"
            >
              <slot
                name="row"
                :item="element"
                :index="index"
              />
            </div>
          </template>
        </Draggable>
      </template>
      <template v-else-if="items.length">
        <div
          v-for="(item, index) in items"
          :key="rowKey(item, index)"
          :class="[$style.item, lastRowClass(index)]"
          :style="rowStyle"
          v-test="rowTestId"
        >
          <slot
            name="row"
            :item="item"
            :index="index"
          />
        </div>
      </template>
      <div
        v-else-if="$slots.empty"
        :class="[$style.item, lastRowClass(0, 1)]"
        :style="rowStyle"
      >
        <slot name="empty" />
      </div>
      <div
        v-if="$slots.footer"
        :class="[$style.item, $style.footerItem]"
        :style="rowStyle"
      >
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { computed, useCssModule, useSlots } from 'vue';
import Draggable from 'vuedraggable';
import ChevronAnimated from '@/components/_shared/ChevronAnimated.vue';
import { lightColor } from '@/helpers/formatting';

type MoreOption =
  | string
  | { name: string; icon?: string; label?: string; routerLink?: unknown };

const props = withDefaults(
  defineProps<{
    color?: string | null;
    expanded?: boolean;
    items?: unknown[];
    draggable?: boolean;
    dragDisabled?: boolean;
    itemKey?: string;
    testId?: string;
    moreOptions?: MoreOption[];
    moreColor?: 'primary' | 'dark';
    showChevron?: boolean;
  }>(),
  {
    color: null,
    expanded: false,
    items: () => [],
    draggable: false,
    dragDisabled: false,
    itemKey: 'id',
    testId: 'expandable-list-item',
    moreOptions: undefined,
    moreColor: 'dark',
    showChevron: true,
  },
);

const rowTestId = computed(() => `${props.testId}-row`);

defineEmits<{
  click: [event: MouseEvent];
  'update:items': [items: unknown[]];
  moreSelect: [action: string];
}>();

const backgroundColor = computed(() =>
  props.color ? lightColor(props.color) : null,
);

const headerStyle = computed(() => {
  if (!props.color) {
    return null;
  }

  return {
    'background-color': backgroundColor.value,
    'border-left-color': props.color,
  };
});

const rowStyle = computed(() =>
  backgroundColor.value ? { 'border-color': backgroundColor.value } : null,
);

const chevronColor = computed(() => (props.color ? 'fixed' : undefined));

const onMoreClick = (e: Event) => {
  e.stopPropagation();
};

const rowKey = (item: unknown, index: number) =>
  (item as Record<string, unknown>)?.[props.itemKey] ?? index;

const cssModule = useCssModule();
const slots = useSlots();
const hasFooter = computed(() => !!slots.footer);

const lastRowClass = (index: number, total: number = props.items.length) => {
  if (index !== total - 1) {
    return null;
  }

  return hasFooter.value ? cssModule.beforeFooter : cssModule.lastItem;
};
</script>

<style
  lang="scss"
  module
>
.base {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-left-width: 3px;
  border-left-style: solid;
  border-radius: $radius;
  padding: $spacing;
  cursor: pointer;
  min-height: 60px;
  gap: $spacing;

  &.expanded {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.section {
  display: flex;
  align-items: center;
  gap: $spacing * 0.5;
}

.sectionLeft {
  min-width: 0;
  overflow: hidden;
}

.sectionRight {
  flex-shrink: 0;
  gap: $spacing;
}

.more {
  display: flex;
  align-items: center;
}

.item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-width: 1px;
  border-style: solid;
  border-top-width: 0;
  min-height: 60px;
  background-color: var(--color-bg);

  &:global(.sortable-chosen) {
    border-top-width: 1px;
    margin-top: -1px;
    margin-bottom: -1px;

    & + .item {
      border-top-width: 1px;
    }
  }
}

.lastItem {
  border-bottom-left-radius: $radius;
  border-bottom-right-radius: $radius;
}

.beforeFooter {
  &:global(.sortable-chosen) {
    margin-bottom: 0;
  }
}

.footerItem {
  border-bottom-left-radius: $radius;
  border-bottom-right-radius: $radius;
}
</style>
